import { logger } from "@/utils/logger";
import React, { useState, useEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, FileText, Download } from "lucide-react";
import { decodeText } from '@/utils/textDecoding';
import { isPlaceholderImage } from '@/utils/imageHashDetection';

interface OEMContentImage {
  url: string;
  image_description: string;
}

interface OEMContentMessageProps {
  images?: OEMContentImage[];
  summary?: string;
  "360_images"?: string[];
  brochure_url?: string;
}

const OEMContentMessage: React.FC<OEMContentMessageProps> = ({ images, summary, "360_images": tour360Images, brochure_url }) => {
  // Filter out images from d2cmedia.ca (these are placeholder/fallback images).
  // Memoized because the placeholder-detection effect below depends on this array:
  // as a bare literal it was a new reference on every render, so the effect re-ran
  // after every commit, called setPlaceholderImages with a fresh Set, and looped
  // unboundedly. Its cleanup also cancelled the 5.1s `completeTimeout` each pass,
  // so `imageCheckComplete` never became true and the gallery never rendered.
  const filteredImages = React.useMemo(
    () => images?.filter(img => !img.url?.startsWith('https://carimagescdn.d2cmedia.ca')) ?? [],
    [images]
  );
  
  // Check if brochure_url is a valid PDF URL
  const hasBrochure = brochure_url && brochure_url.trim() !== '' && brochure_url.toLowerCase().endsWith('.pdf');
  
  const handleBrochureClick = () => {
    if (hasBrochure) {
      window.open(brochure_url, '_blank');
    }
  };
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<number>>(new Set());
  const [imageLoadSuccess, setImageLoadSuccess] = useState<Set<number>>(new Set());
  const [placeholderImages, setPlaceholderImages] = useState<Set<number>>(new Set());
  const [imageCheckComplete, setImageCheckComplete] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Mirror of imageLoadSuccess for the timeout callbacks below. The effect used to
  // depend on `imageLoadSuccess` directly, which re-armed every image timeout each
  // time an image finished loading — while the callback still read a stale copy
  // from its closure. A ref gives the callbacks the live value without making the
  // effect re-run.
  const imageLoadSuccessRef = React.useRef(imageLoadSuccess);
  imageLoadSuccessRef.current = imageLoadSuccess;

  // Check for placeholder images and set timeouts for loading on mount and when images change
  useEffect(() => {
    if (filteredImages.length === 0) {
      setImageCheckComplete(true);
      return;
    }
    
    const timeouts: NodeJS.Timeout[] = [];
    
    const checkPlaceholders = async () => {
      const placeholders = new Set<number>();
      
      for (let i = 0; i < filteredImages.length; i++) {
        const img = filteredImages[i];
        if (img.url && img.url.trim() !== '') {
          const isPlaceholder = await isPlaceholderImage(img.url);
          if (isPlaceholder) {
            placeholders.add(i);
            logger.log('Detected placeholder image, skipping:', img.url);
          }
        }
      }
      
      setPlaceholderImages(placeholders);
    };
    
    checkPlaceholders();
    
    // Set timeout for each image - if it doesn't load within 5 seconds, mark it as failed
    filteredImages.forEach((img, index) => {
      if (img.url && img.url.trim() !== '') {
        const timeout = setTimeout(() => {
          setImageLoadErrors(prev => {
            // Only add to errors if it hasn't successfully loaded
            if (imageLoadSuccessRef.current.has(index)) return prev;
            if (prev.has(index)) return prev;
            const newSet = new Set(prev);
            newSet.add(index);
            logger.log('Image load timeout, marking as failed:', img.url);
            return newSet;
          });
        }, 5000); // 5 second timeout
        
        timeouts.push(timeout);
      }
    });
    
    // Mark check as complete after timeouts are set
    const completeTimeout = setTimeout(() => {
      setImageCheckComplete(true);
    }, 5100); // Slightly after the image timeout
    timeouts.push(completeTimeout);
    
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [filteredImages]);
  
  // Filter out images with empty URLs, those that failed to load, placeholders, and those that haven't loaded yet
  const validImages = filteredImages.length > 0 
    ? filteredImages.filter((img, index) => 
        img.url && 
        img.url.trim() !== '' && 
        !imageLoadErrors.has(index) && 
        !placeholderImages.has(index) &&
        imageLoadSuccess.has(index)
      )
    : [];
  const hasValidImages = validImages.length > 0;

  // Reset index if it's out of bounds
  React.useEffect(() => {
    if (currentImageIndex >= validImages.length && validImages.length > 0) {
      setCurrentImageIndex(0);
    }
  }, [validImages.length, currentImageIndex]);
  
  // Don't render if there's no content to show OR if image check is not complete yet
  if (!imageCheckComplete && !summary) {
    return null;
  }
  
  // Don't render if check is complete but no valid content exists
  if (imageCheckComplete && !hasValidImages && !summary) {
    return null;
  }

  const handleImageError = (imageUrl: string) => {
    // Find the index in the filtered images array and mark it as failed
    const originalIndex = filteredImages.findIndex(img => img.url === imageUrl);
    if (originalIndex !== -1) {
      setImageLoadErrors(prev => new Set(prev).add(originalIndex));
    }
  };

  const handleImageLoad = (imageUrl: string) => {
    // Find the index in the filtered images array and mark it as successfully loaded
    const originalIndex = filteredImages.findIndex(img => img.url === imageUrl);
    if (originalIndex !== -1) {
      setImageLoadSuccess(prev => new Set(prev).add(originalIndex));
    }
  };

  const goToNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % validImages.length);
  };

  const goToPrevious = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + validImages.length) % validImages.length);
  };

  const renderImageContent = () => {
    // Don't render if image check is not complete yet
    if (!imageCheckComplete) return null;
    
    // Don't render if no valid images or all images have failed to load
    if (!hasValidImages) {
      logger.log('❌ OEMContentMessage: No valid images to display, skipping carousel');
      return null;
    }

    const currentImage = validImages[currentImageIndex];
    const decodedDescription = decodeText(currentImage.image_description);
    const showNavigation = validImages.length > 1;

    return (
      <div className="relative w-full max-w-3xl mx-auto">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow border-0 shadow-none">
                <img
                  src={currentImage.url}
                  alt={decodedDescription}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  onLoad={() => handleImageLoad(currentImage.url)}
                  onError={() => handleImageError(currentImage.url)}
                />
              </Card>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p>{decodedDescription}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {showNavigation && (
          <>
            <Button
              variant="secondary"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-background/80 hover:bg-background shadow-md z-10"
              onClick={(e) => goToPrevious(e)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <Button
              variant="secondary"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-background/80 hover:bg-background shadow-md z-10"
              onClick={(e) => goToNext(e)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
              {validImages.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                  className={`h-2 rounded-full transition-all ${
                    index === currentImageIndex 
                      ? 'w-6 bg-primary' 
                      : 'w-2 bg-background/60 hover:bg-background/80'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="w-full max-w-full overflow-hidden min-w-0 space-y-4">
      {/* Hidden images to preload and detect which ones are valid */}
      {filteredImages.map((img, index) => (
        <img
          key={`preload-${index}`}
          src={img.url}
          alt=""
          className="hidden"
          onLoad={() => handleImageLoad(img.url)}
          onError={() => handleImageError(img.url)}
        />
      ))}
      
      {renderImageContent()}
      
      {hasBrochure && (
        <Card 
          className="p-4 cursor-pointer hover:bg-accent/50 transition-colors group border border-border/50"
          onClick={handleBrochureClick}
        >
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-12 h-14 bg-destructive/10 rounded-lg flex items-center justify-center">
              <FileText className="h-7 w-7 text-destructive" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground text-sm">Product Brochure</p>
              <p className="text-xs text-muted-foreground truncate">{brochure_url}</p>
            </div>
            <div className="flex-shrink-0">
              <Download className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
          </div>
        </Card>
      )}
      
      {summary && summary.trim() !== '' && (
        <div className="text-sm text-foreground/90 leading-relaxed">
          {decodeText(summary)}
        </div>
      )}
      
      {/* 360_images are ignored for now - will be handled with proper 360 viewer later */}
    </div>
  );
};

export default OEMContentMessage;
